import React, { useEffect, useRef, useState } from "react";
import { useInterview } from "./interview.context";
import { InterViewCurrentTask, InterviewInitState } from "../types/interview.type";
import { SessionStorage } from "../types/general.type";
import { FaHandPointRight } from "react-icons/fa";
import { IoSend } from "react-icons/io5";
import { Mic } from "lucide-react";
import { toast } from "react-toastify";

interface Props {
  iconSize: number;
}

const SpeechToText = ({ iconSize }: Props) => {
  const { interview, updateInterview, finalTranscript, setFinalTranscript, recognition, setRecognition, sendMessage } = useInterview();
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const silenceTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    recordingStarted,
    setUp,
    currentTask,
    isEnabledRecordButton,
    isFullScreen,
    isOnline
  } = interview;

  // const location = useLocation();
  const state = JSON.parse(sessionStorage.getItem(SessionStorage.INTERVIEW_STATE) || "{}") as InterviewInitState;
  const { initialResponse, selectedInterviweer, setup } = state || {};
  const [silenceTime, setSilenceTime] = useState(0);
  const warnedRef = useRef(false);
  const recognitionEnabledRef = useRef(false);



  useEffect(() => {
    if (
      initialResponse?.response == null ||
      initialResponse.threadId == null ||
      selectedInterviweer == null || !isOnline
    ) {
      return;
    }
    startRecognition();
  }, []);



  useEffect(() => {
    if (recordingStarted && isOnline)
      recognitionEnabledRef.current = true;
    else recognitionEnabledRef.current = false;
  }, [recordingStarted, isOnline]);



  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && recordingStarted) {
        startListening();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [recordingStarted]);

  const startRecognition = (start: boolean = false) => {
    if ("SpeechRecognition" in window || "webkitSpeechRecognition" in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const SpeechGrammarList = window.SpeechGrammarList || window.webkitSpeechGrammarList;
      const recognition = new SpeechRecognition();
      const speechRecognitionList = new SpeechGrammarList();

      recognition.grammars = speechRecognitionList;
      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.lang = "en";
      recognition.maxAlternatives = 1;

      recognition.onresult = (event: any) => {

        if (!recognitionEnabledRef.current) {
          return;
        }
        if (!finalTranscript && event.results.length === 0) return;
        setListening(true);
        setSilenceTime(0);
        const lastItem = event.results[event.results.length - 1];
        const transcript = lastItem[0].transcript;
        setTranscript(transcript);

      };

      recognition.onspeechend = () => {
      };

      recognition.onaudiostart = () => {
        // Audio started
      };

      recognition.onend = () => {
        const isOnline = navigator.onLine;
        const listening = sessionStorage.getItem(SessionStorage.LISTENING);
        if (listening && isOnline && document.visibilityState === "visible") {
          startListening();
          return;
        }
        setListening(false);
        setSilenceTime(0);
      };

      recognition.onerror = (event: { error: string }) => {
        const isBrave = (navigator as any).brave?.isBrave();
        if (isBrave && !warnedRef.current && (event.error === "network" || event.error === "no-speech")) {
          toast.error("Brave browser detected. Please disable Shields for this site to use speech recognition..");
          warnedRef.current = true;
          return;
        }
        if (!warnedRef.current && (event.error === "network" || event.error === "no-speech")) {
          // const id = toast.info(
          //   "You are offline. Waiting for connection to retry...",
          //   { autoClose: false }
          // );
          warnedRef.current = true;
          return;
        }
      };

      setRecognition(recognition);
      if (start) {
        recognition.start();
      }
    } else {
      updateInterview({ isSpeechRecognitionAvailable: false });
    }
  };

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (listening && state.setup.isAuto) {
      timer = setInterval(() => {
        setSilenceTime((prev) => prev + 1);
      }, 1000);
      silenceTimer.current = timer;
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [listening]);

  useEffect(() => {
    if (
      silenceTime > 6 &&
      finalTranscript &&
      listening &&
      currentTask === InterViewCurrentTask.YOU_SPEAKING
    ) {
      stopListening();
    }
  }, [silenceTime]);

  useEffect(() => {

    const isBotSpeaking = currentTask === InterViewCurrentTask.BOT_SPEAKING;
    if (isBotSpeaking) {
      setTranscript("");
      return;
    }
    if (transcript) {
      setFinalTranscript((prev) => prev + " " + transcript);
      updateInterview({
        isEnabledSubmitButton: true,
        currentTask: InterViewCurrentTask.YOU_SPEAKING,
      });
      setTranscript("");
    }
  }, [transcript]);

  useEffect(() => {
    if (recordingStarted && isOnline) {
      try {
        startListening();
      } catch (error) {
      }
    }
    else {
      recognition?.stop()
    }
  }, [recordingStarted, isOnline]);

  useEffect(() => {
    if (setUp?.completed) {
      stopListening();
      setFinalTranscript("");
    }
  }, [setUp?.completed]);

  const startListening = async () => {
    try {
      if (!recognition) {
        startRecognition(true);
      } else {
        recognition.start();
      }
    } catch (err) {
    }
  };

  const stopListening = async () => {
    if (!isOnline) {

      return;
    }
    setListening(false);
    if (silenceTimer.current) {
      clearInterval(silenceTimer.current);
      silenceTimer.current = null;
    }

    const trimmedText = finalTranscript.trim();
    if (trimmedText !== "") {
      updateInterview({
        currentTask: InterViewCurrentTask.PROCESSING_ANSWER,
        recordingStarted: false,
      });
      setFinalTranscript("");
      await sendMessage(trimmedText);
    }
  };

  return (
    <>
      {!setUp?.isAuto && recordingStarted && finalTranscript && (
        <div className="relative ml-[8px] flex items-center">
          {isOnline && <FaHandPointRight
            className="text-black dark:text-white transform -translate-x-1/2  animate-wiggle"
            size={20}
          />}
          <button
            onClick={stopListening}
            className={`w-12 h-12 flex rounded-full  items-center justify-center ${isFullScreen ? "bg-gray-500" : "bg-gray-700"}`}
          >
            <IoSend size={iconSize} className="text-white" />
          </button>
        </div>
      )}
      {!recordingStarted && !setUp?.isAuto && isEnabledRecordButton && (
        <div className="relative ml-[8px] flex items-center">
          {isOnline && <FaHandPointRight
            className="text-black dark:text-white transform -translate-x-1/2  animate-wiggle"
            size={20}
          />}
          <button
            disabled={!isOnline}
            onClick={() => {
              sessionStorage.setItem(SessionStorage.LISTENING, "true");
              updateInterview({ recordingStarted: true });
            }}

            className={`w-12 h-12 flex rounded-full  items-center justify-center ${isFullScreen ? "bg-gray-500" : "bg-gray-700"}`}
            title={listening ? "Stop Listening" : "Start Listening"}
            aria-label={listening ? "Stop Listening" : "Start Listening"}
          >
            <Mic size={22} className="text-white" />
          </button>
        </div>
      )
      }
    </>

  );
};

export default SpeechToText;
